<!--
 * 水印组件
 * 功能：为系统添加防篡改水印，显示用户信息、时间、IP地址和位置
 * 作者：智慧云行（成都）科技有限公司
 * 更新时间：2025-12-16
-->
<template>
  <div 
    v-if="visible"
    :id="containerId" 
    class="watermark-container"
    :style="containerStyle">
  </div>
</template>

<script>
import { getIpInfo } from '@/assets/js/ipInfo'

export default {
  name: 'Watermark',
  props: {
    // 水印文本内容（如果不传则自动获取用户信息）
    text: {
      type: [String, Array],
      default: null
    },
    // 水印旋转角度
    rotate: {
      type: Number,
      default: -30
    },
    // 字体大小
    fontSize: {
      type: Number,
      default: 14
    },
    // 水印透明度
    opacity: {
      type: Number,
      default: 0.15
    },
    // 水印颜色
    color: {
      type: String,
      default: '#000000'
    },
    // 水印间距（宽度）
    width: {
      type: Number,
      default: 500
    },
    // 水印间距（高度）
    height: {
      type: Number,
      default: 300
    },
    // z-index层级
    zIndex: {
      type: Number,
      default: 2000
    },
    // 是否显示水印
    visible: {
      type: Boolean,
      default: true
    },
    // 是否自动更新时间
    autoUpdate: {
      type: Boolean,
      default: true
    },
    // 更新间隔（毫秒）
    updateInterval: {
      type: Number,
      default: 60000
    },
    // 容器ID
    containerId: {
      type: String,
      default: 'zhyx-watermark-container'
    }
  },
  data() {
    return {
      watermarkTimer: null,
      resizeObserver: null,
      userInfo: {},
      ipInfo: {}
    }
  },
  computed: {
    containerStyle() {
      return {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: this.zIndex,
        pointerEvents: 'none',
        backgroundImage: `url("${this.watermarkUrl}")`,
        backgroundRepeat: 'repeat',
        opacity: this.opacity,
        display: this.visible ? 'block' : 'none',
        visibility: this.visible ? 'visible' : 'hidden'
      }
    },
    watermarkUrl() {
      const svgContent = this.createWatermarkSvg()
      const base64Svg = btoa(unescape(encodeURIComponent(svgContent)))
      return `data:image/svg+xml;base64,${base64Svg}`
    },
    watermarkTexts() {
      if (this.text) {
        return Array.isArray(this.text) ? this.text : [this.text]
      }
      
      // 自动生成水印文本
      const username = this.userInfo?.name || '智慧云行'
      const timestamp = new Date().toLocaleString('zh-CN')
      const ipAddress = this.ipInfo?.ip || '获取中...'
      const location = this.ipInfo?.province 
        ? `${this.ipInfo.province}-${this.ipInfo.city}-${this.ipInfo.area}`
        : '位置获取中...'
      
      return [
        `用户: ${username}`,
        `时间: ${timestamp}`,
        `IP: ${ipAddress}`,
        `位置: ${location}`
      ]
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.init()
      } else {
        this.destroy()
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.init()
    }
  },
  beforeDestroy() {
    this.destroy()
  },
  methods: {
    /**
     * 初始化水印
     */
    async init() {
      try {
        // 获取用户信息
        this.userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}')
        
        // 获取IP信息
        if (sessionStorage.getItem('ipInfo')) {
          this.ipInfo = JSON.parse(sessionStorage.getItem('ipInfo') || '{}')
        } else {
          this.ipInfo = await getIpInfo()
        }
        
        // 强制更新
        this.$forceUpdate()
        
        // 设置自动更新定时器
        if (this.autoUpdate) {
          this.startAutoUpdate()
        }
        
        // 监听窗口大小变化
        this.observeResize()
        
        // 防止水印被删除
        this.observeDOM()
        
        console.log('[水印组件] 初始化成功')
      } catch (error) {
        console.error('[水印组件] 初始化失败:', error)
      }
    },
    
    /**
     * 创建水印SVG
     */
    createWatermarkSvg() {
      const texts = this.watermarkTexts
      const lineHeight = this.fontSize * 1.5
      const startY = 50
      
      let tspans = texts.map((text, index) => {
        const y = index === 0 ? '-3em' : '1.5em'
        return `<tspan x="50%" dy="${y}">${this.escapeHtml(text)}</tspan>`
      }).join('')
      
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${this.width}" height="${this.height}">
          <text 
            x="50%" 
            y="50%" 
            fill="${this.color}" 
            fill-opacity="0.5" 
            transform="rotate(${this.rotate}, ${this.width/2}, ${this.height/2})" 
            text-anchor="middle" 
            font-size="${this.fontSize}" 
            font-weight="bold" 
            font-family="'PingFang SC', 'Microsoft YaHei', sans-serif">
            ${tspans}
          </text>
        </svg>`
    },
    
    /**
     * HTML转义
     */
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }
      return String(text).replace(/[&<>"']/g, m => map[m])
    },
    
    /**
     * 开始自动更新
     */
    startAutoUpdate() {
      if (this.watermarkTimer) {
        clearInterval(this.watermarkTimer)
      }
      
      this.watermarkTimer = setInterval(() => {
        this.$forceUpdate()
      }, this.updateInterval)
    },
    
    /**
     * 监听窗口大小变化
     */
    observeResize() {
      const debounce = (func, wait) => {
        let timeout
        return function(...args) {
          clearTimeout(timeout)
          timeout = setTimeout(() => func.apply(this, args), wait)
        }
      }
      
      window.addEventListener('resize', debounce(() => {
        this.$forceUpdate()
      }, 300))
    },
    
    /**
     * 监听DOM变化，防止水印被删除
     */
    observeDOM() {
      if (typeof MutationObserver === 'undefined') return
      
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const container = document.getElementById(this.containerId)
            if (!container && this.visible) {
              console.warn('[水印组件] 水印容器被删除，正在恢复...')
              this.$nextTick(() => {
                this.$forceUpdate()
              })
            }
          }
        })
      })
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      })
      
      this.resizeObserver = observer
    },
    
    /**
     * 销毁水印
     */
    destroy() {
      // 清除定时器
      if (this.watermarkTimer) {
        clearInterval(this.watermarkTimer)
        this.watermarkTimer = null
      }
      
      // 断开观察器
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      
      console.log('[水印组件] 已销毁')
    },
    
    /**
     * 手动更新水印
     */
    update() {
      this.$forceUpdate()
    },
    
    /**
     * 清除水印
     */
    clear() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="less" scoped>
.watermark-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
  overflow: hidden;
}
</style>
